import {
  Beaker20Regular,
  Chat20Regular,
  ClipboardTaskListLtr20Regular,
  DataBarHorizontal20Regular,
  DocumentBulletList20Regular,
  DocumentMultiple20Regular,
  History20Regular,
  Megaphone20Regular,
  Money20Regular,
  People20Regular,
  PersonAccounts20Regular,
  ReceiptMoney20Regular,
  Settings20Regular,
  WrenchScrewdriver20Regular,
} from '@fluentui/react-icons';
import { Button, Tooltip } from '@fluentui/react-components';
import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { isDemoMode } from '@/lib/demo-mode';

type Staff = { name: string; role: 'staff' | 'admin' };

type NavItem = {
  label: string;
  to: string;
  icon: ReactNode;
  adminOnly?: boolean;
};

const primary: NavItem[] = [
  { label: 'Collection List', to: '/collections', icon: <ReceiptMoney20Regular /> },
  { label: 'Client Lists', to: '/clients', icon: <People20Regular /> },
  { label: 'Account History', to: '/history', icon: <History20Regular /> },
  { label: 'Unpaid Accounts', to: '/unpaid', icon: <DocumentBulletList20Regular /> },
  { label: 'Paid Accounts', to: '/paid', icon: <DocumentMultiple20Regular /> },
  { label: 'Payment Review', to: '/payment-review', icon: <Money20Regular /> },
];

const secondary: NavItem[] = [
  { label: 'Applications', to: '/applications', icon: <PersonAccounts20Regular /> },
  { label: 'Support', to: '/support', icon: <Chat20Regular /> },
  { label: 'Messaging', to: '/messaging', icon: <Megaphone20Regular /> },
  { label: 'Reports', to: '/reports', icon: <DataBarHorizontal20Regular /> },
  { label: 'Expenses', to: '/expenses', icon: <ClipboardTaskListLtr20Regular /> },
  { label: 'Content', to: '/content', icon: <WrenchScrewdriver20Regular /> },
];

const administration: NavItem[] = [
  { label: 'System Health', to: '/system-health', icon: <Settings20Regular />, adminOnly: true },
  { label: 'Staging Tests', to: '/testing', icon: <Beaker20Regular />, adminOnly: true },
];

function NavigationGroup({
  items,
  label,
  role,
}: {
  items: NavItem[];
  label: string;
  role: Staff['role'];
}) {
  const visibleItems = items.filter((item) => !item.adminOnly || role === 'admin');
  if (visibleItems.length === 0) return null;
  return (
    <section className="fc-nav-group" aria-label={label}>
      <span className="fc-nav-group-title">{label}</span>
      {visibleItems.map((item) => (
        <NavLink
          className="fc-nav-link"
          key={item.to}
          to={item.to}
          title={item.label}
        >
          {item.icon}
          <span>{item.label}</span>
        </NavLink>
      ))}
    </section>
  );
}

function pageName(pathname: string): string {
  const match = [...primary, ...secondary, ...administration].find(
    (item) => pathname === item.to || pathname.startsWith(`${item.to}/`),
  );
  return match?.label ?? 'FastCloud Admin';
}

export function AdminShell({
  children,
  onSignOut,
  staff,
}: {
  children: ReactNode;
  onSignOut: () => Promise<void>;
  staff: Staff;
}) {
  const location = useLocation();

  return (
    <div className="fc-app-shell">
      <aside className="fc-sidebar" aria-label="Staff navigation">
        <div className="fc-brand">
          <span className="fc-brand-mark" aria-hidden="true">i</span>
          <span>FastCloud Admin</span>
        </div>
        <nav className="fc-nav-scroll">
          <NavigationGroup items={primary} label="Collections" role={staff.role} />
          <NavigationGroup items={secondary} label="Operations" role={staff.role} />
          <NavigationGroup items={administration} label="Administration" role={staff.role} />
        </nav>
      </aside>
      <header className="fc-topbar">
        <div className="fc-topbar-title">{pageName(location.pathname)} · Manila operations</div>
        <div className="fc-topbar-user">
          <span>{staff.name}</span>
          <span className="fc-topbar-user-role">{staff.role === 'admin' ? 'Administrator' : 'Staff'}</span>
          <Tooltip content="Sign out of the staff console" relationship="label">
            <Button appearance="subtle" onClick={() => void onSignOut()} size="small">
              Sign out
            </Button>
          </Tooltip>
        </div>
      </header>
      <main className="fc-main" id="main-content" tabIndex={-1}>
        {isDemoMode ? (
          <div className="fc-demo-banner" role="status">
            <strong>Sample data · SMS simulated</strong>
            <span>Balances and client records here are examples. No text messages are sent to phones.</span>
          </div>
        ) : null}
        {children}
      </main>
    </div>
  );
}
